import './../style.scss';
import './BriefContract.scss';
import { formatCash, formatDate, calcTotalWithTax, calcPlatformFee, calcClientPlatformFee, calcSponsorServiceFee, calcSponsorVat, calcClientVat, calcTotalPayableBySponsor, calcProfessionalFee, findSelectedProfessional, findQuoteByProfessional, financialCalculations } from './../../../../utils/sharedFunctions.js';
import { useState, useEffect } from 'react'; // Import useEffect for potential side effects

const BriefContract = ({ project }) => {
  const [selectedBrief, setSelectedBrief] = useState(project);

  useEffect(() => {
    setSelectedBrief(project);
  }, [project]);

  // Memoize the calculated values to avoid unnecessary recalculations
  const totalProjectCost = formatCash(calcTotalWithTax(selectedBrief?.budgetEstimation));

  const selectedProfessional = findSelectedProfessional(selectedBrief?.shortlist);
  const quoteByProfessional = findQuoteByProfessional(
    selectedBrief?.quote,
    selectedProfessional?.professional?._id
  );

  // Raw calculations
  const rawProfessionalFee = calcProfessionalFee(quoteByProfessional?.billables);
  const rawCredippleFee = calcPlatformFee(rawProfessionalFee);
  const rawSponsorCredippleFee = calcSponsorServiceFee(rawProfessionalFee);
  const rawSponsorVatFee = calcSponsorVat(rawProfessionalFee);
  const rawClientVatFee = calcClientVat(rawProfessionalFee);
  const rawAmountReceivable = rawProfessionalFee - rawCredippleFee;
  const rawTotalPayableBySponsor = calcTotalPayableBySponsor(rawCredippleFee);
  const rawClientPlatformFee = calcClientPlatformFee(rawProfessionalFee); // <-- correct usage

  // Formatted values
  const professionalFee = formatCash(rawProfessionalFee);
  const credippleFee = formatCash(rawCredippleFee);
  const sponserCredippleFee = formatCash(rawSponsorCredippleFee);
  const sponsorVatFee = formatCash(rawSponsorVatFee);
  const clientVatFee = formatCash(rawClientVatFee);
  const amountReceivable = formatCash(rawAmountReceivable);
  const totalPayableBySponsor = formatCash(rawTotalPayableBySponsor);
  const clientPlatformFee = formatCash(rawClientPlatformFee); // <-- formatted after calculation

  const Finances = financialCalculations(rawProfessionalFee, selectedBrief);

  return (
    <div className='brief-contract'>
      <div className='summary-info'>
        <div className='summary-header'>
          <h2>Project Summary</h2>
        </div>
        <div className='summary-text'>
          <p>Please note that this is just a summary, but you can find the full version of your contract here.</p>
        </div>

        <div className='row'>
          <div className='value-holder'>
            <div className='summary-header'>
              <h2>Total Project Cost</h2>
            </div>
            <div className='value'>
              {`ZAR ${formatCash(rawProfessionalFee < 2500 ?   (rawProfessionalFee + 250) : Finances.ClientSubTot * 0.15 + (Finances.ClientSubTot))}`}
            </div>
          </div>

          <div className='value-holder'>
            <div className='summary-header'>
              <h2>Credipple Fee</h2>
            </div>
            <div className='value'>
              {`ZAR ${Number(rawProfessionalFee) < Number(2500) ? formatCash(250) : Finances.Remaining2BPayByClient * 0.1}`}
            </div>
          </div>
        </div>

        <div className='row'>
          <div className='value-holder'>
            <div className='summary-header'>
              <h2>Category of Service</h2>
            </div>
            <div className='value'>
              {selectedBrief?.typeOfProfessional && selectedBrief.typeOfProfessional.length > 0 ? (
                <div>
                  {selectedBrief.typeOfProfessional.map((typeOfProfessional, index) => (
                    <div key={index}>{typeOfProfessional}</div>
                  ))}
                </div>
              ) : (
                <div>
                  {["No Data"].map((typeOfProfessional, index) => (
                    <div key={index}>{typeOfProfessional}</div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className='value-holder'>
            <div className='summary-header'>
              <h2>Skills of Professional</h2>
            </div>
            <div className='value'>
              {selectedBrief?.skillsOfProfessional && selectedBrief.skillsOfProfessional.length > 0 ? (
                <div>
                  {selectedBrief.skillsOfProfessional.map((professionalType, index) => (
                    <div key={index}>{professionalType}</div>
                  ))}
                </div>
              ) : (
                <div>
                  {["No Data"].map((skillsOfProfessional, index) => (
                    <div key={index}>{skillsOfProfessional}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='row'>
          <div className='value-holder'>
            <div className='summary-header'>
              <h2>Project Start Date</h2>
            </div>
            <div className='value'>
              {formatDate(selectedBrief?.projectPlan[0]?.projStartDate)}
            </div>
          </div>

          <div className='value-holder'>
            <div className='summary-header'>
              <h2>Project End Date</h2>
            </div>
            <div className='value'>
              {formatDate(selectedBrief?.projectPlan[0]?.projEndDate)}
            </div>
          </div>
        </div>

        <div className='milestone-grid'>
          {selectedBrief?.quote && selectedBrief.quote.length > 0 ? (
            selectedBrief.quote
              .filter(quoteItem => quoteItem?.milestone) // Only include ones with milestone
              .map((quoteItem, index) => (
                <div className='milestone-value-holder' key={index}>
                  <div className='summary-header'>
                    <h2>Milestone {index + 1}</h2>
                  </div>
                  <div className='value'>
                    {quoteItem.milestone}
                  </div>
                </div>
              ))
          ) : (
            ""
          )}
        </div>

        <div className='row descriptions-holder'>
          <div className='description-holder'>
            <div className='summary-header'>
              <h2>Description</h2>
            </div>
            <div className='text-info'>
              {selectedBrief?.briefDescription || 'No description provided.'}
            </div>
          </div>
          <div className='deliverables-holder'>
            <div className='summary-header'>
              <h2>Deliverables & Responsibilities</h2>
            </div>
            <div className='text-info'>
              {selectedBrief?.deliverables || 'No deliverables provided.'}
            </div>
          </div>
        </div>
      </div>
      <div className='outer-quotes-info'>
        <div className='summary-header'>
          <h2>Quote for Project</h2>
        </div>

        <div className='quotes-holder'>
          <div className='quote'>
            <div className='summary-header'>
              <h2>Amount Payable to Professional:</h2>
            </div>

            <div className='quote-info'>
              <div className='quote-data'>
                <div className='quote-data-name'>Professional service fee:</div>
                <div className='quote-data-value'>{`ZAR ${professionalFee}`}</div>
              </div>

              <div className='quote-data'>
                <div className='quote-data-name'>Credipple platform fee (10%):</div>
                <div className='quote-data-value'> {`ZAR ${Number(rawProfessionalFee) < Number(2500) ? formatCash(250) : Finances.Remaining2BPayByClient * 0.1}`}</div>
              </div>

              <div className='quote-data'>
                <div className='quote-data-total'>Total amount Receivable:</div>
                <div className='quote-data-total-value'> {`ZAR ${rawProfessionalFee < 2500 ? rawProfessionalFee - 250 : amountReceivable}`}</div>
              </div>
            </div>
          </div>

          <div className='seperator'></div>

          {Finances.voucherAmntDebited > 0 &&
            <div className='quote'>
              <div className='summary-header'>
                <h2>Amount to be invoiced to Sponsor:</h2>
              </div>

              <div className='quote-info'>
                <div className='quote-data'>
                  <div className='quote-data-name'>Professional service fee:</div>
                  <div className='quote-data-value'>{`ZAR ${professionalFee}`}</div>
                </div>

                <div className='quote-data'>
                  <div className='quote-data-name'>Credipple service fee (15%):</div>
                  <div className='quote-data-value'>{`ZAR ${sponserCredippleFee}`}</div>
                </div>

                <div className='quote-data'>
                  <div className='quote-data-name'>VAT (15%):</div>
                  <div className='quote-data-value'>{`ZAR ${sponsorVatFee}`}</div>
                </div>

                <div className='quote-data'>
                  <div className='quote-data-total'>Total amount payable by Sponsor:</div>
                  <div className='quote-data-total-value'> {`ZAR ${totalPayableBySponsor}`}</div>
                </div>
              </div>
            </div>
          }
          <div className='seperator'></div>

          <div className='quote'>
            <div className='summary-header'>
              <h2>Amount to be invoiced to Client:</h2>
            </div>

            <div className='quote-info'>
              <div className='quote-data'>
                <div className='quote-data-name'>Professional service fee:</div>
                <div className='quote-data-value'>{`ZAR ${professionalFee}`}</div>
              </div>

              <div className='quote-data'>
                <div className='quote-data-name'>Voucher amount debited</div>
                <div className='quote-data-value'>{`ZAR ${formatCash(Finances.voucherAmntDebited)}`}</div>
              </div>

              <div className='quote-data'>
                <div className='quote-data-name'>Remaining balance due:</div>
                <div className='quote-data-value'>{`ZAR ${formatCash(Finances.Remaining2BPayByClient)}`}</div>
              </div>

              <div className='quote-data'>
                <div className='quote-data-name'>Credipple Platform fee (10%):</div>
                <div className='quote-data-value'>{`ZAR ${formatCash(Finances.CredFee10)}`}</div>
              </div>

              <div className='quote-data'>
                <div className='quote-data-name'>Subtotal</div>
                <div className='quote-data-value'>{`ZAR ${formatCash(rawProfessionalFee < 2500 ? rawProfessionalFee + 250 : Finances.ClientSubTot)}`}</div>
              </div>

              <div className='quote-data'>
                <div className='quote-data-name'>VAT (15%):</div>
                <div className='quote-data-value'>{`ZAR ${formatCash(rawProfessionalFee < 2500 ? (rawProfessionalFee + 250) * 0.15 : Finances.ClientSubTot * 0.15)}`}</div>
              </div>

              <div className='quote-data'>
                <div className='quote-data-total'>Total amount payable by Client:</div>
                <div className='quote-data-total-value'>{`ZAR ${formatCash(rawProfessionalFee < 2500 ? (rawProfessionalFee + 250) * 0.15 + (rawProfessionalFee + 250) : Finances.ClientSubTot * 0.15 + (Finances.ClientSubTot))}`}</div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default BriefContract;